<script setup lang="ts">
import { computed, ref } from "vue";

import {
  createCustomProperty,
  getPropertyConfig,
  getPropertyDisplayTitle,
  getPropertyWithConfigList,
  showPropertiesWithConfig,
} from "~/common/properties";
import Button from "~/components/dumb/Button.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import MultiselectDropdownMenu from "~/components/dumb/MultiselectDropdownMenu.vue";
import PropertyDropdownMenuItem from "~/components/dumb/PropertyDropdownMenuItem.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import UpgradeConfirmationDialog from "~/components/dumb/UpgradeConfirmationDialog.vue";
import { NOT_SHOWN_PROPERTY_KINDS } from "~/constants/property";
import { DotsHorizontalIcon, PlusIcon } from "~/icons";
import {
  ButtonSize,
  ButtonStyle,
  DropdownMenuItemKind,
  Entitlement,
  Placement,
  PropertyKind,
  Subscription,
} from "~/shared/enums";
import type { DropdownMenuSection } from "~/shared/types";
import { useAppStore, useDataStore, useTenantStore } from "~/stores";
import { getItemCountText } from "~/utils/common";
import { makePropertyComparator } from "~/utils/comparator";

defineProps<{
  isDotsMenu?: boolean;
}>();

const emit = defineEmits<{
  createProperty: [];
  setPropertyDuidVisibility: [propertyDuid: string, shown: boolean, propertyOrderDuids?: string[]];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const tenantStore = useTenantStore();

const searchValue = ref("");

const upgradeDialog = ref<InstanceType<typeof UpgradeConfirmationDialog> | null>(null);
const menu = ref<InstanceType<typeof DropdownMenu> | null>(null);

const allProperties = computed(() => {
  const comparator = makePropertyComparator([]);
  return dataStore.propertyList
    .map((p) => ({ ...p, config: getPropertyConfig(p.kind) }))
    .sort((a, b) => (a.hidden || b.hidden ? 1 : comparator(a.duid, b.duid)));
});

// Shown properties
const cachedShownPropertyDuids = ref<Set<string>>(new Set());
const shownPropertiesWithConfig = computed(() => showPropertiesWithConfig([...cachedShownPropertyDuids.value]));
const shownPropertyDuids = computed(
  () => new Set(shownPropertiesWithConfig.value.map(({ property }) => property.duid))
);
cachedShownPropertyDuids.value = new Set([...shownPropertyDuids.value]);

/* Custom properties */
const customProperties = computed(() => allProperties.value.filter((property) => !property.config.isDefault));
const maxCustomProperties = computed(() => tenantStore.getEntitlementValue(Entitlement.MAX_CUSTOM_PROPERTIES));
const upgradeRequired = computed(() => customProperties.value.length >= maxCustomProperties.value);

const addShownProperty = (propertyDuid: string) => {
  const propertyOrderDuids =
    appStore.propertyOrderDuids.length === 0 ? [...shownPropertyDuids.value] : [...appStore.propertyOrderDuids];

  if (!propertyOrderDuids.includes(propertyDuid)) {
    // Place before last locked property
    const lastLockedPropertyIndex = shownPropertiesWithConfig.value.findLastIndex(({ locked }) => locked);
    if (lastLockedPropertyIndex === -1) {
      propertyOrderDuids.push(propertyDuid);
    } else {
      propertyOrderDuids.splice(lastLockedPropertyIndex, 0, propertyDuid);
    }
  }

  emit("setPropertyDuidVisibility", propertyDuid, true, propertyOrderDuids);
  cachedShownPropertyDuids.value = new Set([...shownPropertyDuids.value]);
};

const onCreateCustomProperty = async (kind: PropertyKind, title: string) => {
  if (upgradeRequired.value) {
    upgradeDialog.value?.openModal();
    return;
  }
  const newProperty = await createCustomProperty(kind, title);

  emit("createProperty");

  if (newProperty && newProperty.duid) {
    addShownProperty(newProperty.duid);
  }
};

const propertySections = computed(() => [
  {
    title: "Properties",
    items: getPropertyWithConfigList()
      .filter(
        ([property]) => !shownPropertyDuids.value.has(property.duid) && !NOT_SHOWN_PROPERTY_KINDS.has(property.kind)
      )
      .map(([property, config]) => ({
        value: property.duid,
        label: getPropertyDisplayTitle(property),
        selected: false,
        component: PropertyDropdownMenuItem,
        componentArgs: {
          title: getPropertyDisplayTitle(property),
          icon: config.icon,
          property,
          propertyKind: property.kind,
        },
      })),
  },
]);

const customPropertiesSections = computed<DropdownMenuSection[]>(() => [
  {
    title: "Add custom property",
    items: Object.values(PropertyKind)
      .map((e) => getPropertyConfig(e))
      .filter((e) => !e.isDefault)
      .map((config) => ({
        title: config.label,
        kind: DropdownMenuItemKind.COMPONENT,
        component: PropertyDropdownMenuItem,
        componentArgs: {
          title: config.label,
          icon: config.icon,
          propertyKind: config.kind,
          showTooltipImage: true,
          onClick: () => onCreateCustomProperty(config.kind, searchValue.value),
        },
      })),
  },
]);
</script>

<template>
  <MultiselectDropdownMenu
    placeholder="Add a property"
    :sections="propertySections"
    :placement="Placement.BOTTOM_RIGHT"
    :distance="2"
    has-new-entry-slot
    enable-new-entry
    :class="isDotsMenu && 'px-1'"
    @add="addShownProperty"
    @create="menu?.open()"
    @search="searchValue = $event">
    <button
      v-if="isDotsMenu"
      type="button"
      class="flex items-center rounded-sm p-0.5 text-lt focus-ring-std hover:bg-md">
      <span class="sr-only">Property settings</span>
      <DotsHorizontalIcon class="icon-sm text-vlt" />
    </button>
    <Tooltip v-else text="Show more properties in this layout">
      <Button
        :btn-style="ButtonStyle.SECONDARY"
        :size="ButtonSize.CHIP"
        class="!rounded-full !p-0.5"
        :icon="PlusIcon"
        borderless
        is-contrast
        a11y-label="Property" />
    </Tooltip>
    <UpgradeConfirmationDialog
      ref="upgradeDialog"
      :disabled="!upgradeRequired"
      :feature-action="`create more than ${getItemCountText(maxCustomProperties, 'custom property', { noSpecial: true })}`"
      :subscription="Subscription.PREMIUM"
      no-button />
    <template #newEntrySlot>
      <DropdownMenu
        ref="menu"
        mobile-include-back
        :sections="customPropertiesSections"
        :placement="Placement.RIGHT_TOP"
        class="!w-full"
        :distance="0"
        :width-pixels="180"
        @click.stop>
        <DropdownMenuItemContent :icon="PlusIcon" title="Create a property" is-submenu class="!p-0" />
      </DropdownMenu>
    </template>
  </MultiselectDropdownMenu>
</template>
